/**
 * Application Constants
 * Centralized configuration values that were previously hardcoded
 *
 * Usage:
 * import { APP_CONFIG, CACHE_CONFIG, BREAKPOINTS } from './lib/constants';
 */

// ==================== APP CONFIGURATION ====================
export const APP_CONFIG = {
  // App Info
  name: "Jamaica Schools Guide",
  shortName: "JM Schools Guide",
  version: "2.1.0",

  // URLs
  baseUrl: process.env.REACT_APP_BASE_URL || 'https://jamaicaschoolsguide.com',

  // Contact/Support
  supportEmail: 'info@jamaicaschoolsguide.com',

  // Phone format placeholder (Jamaica)
  phoneFormat: '876-XXX-XXXX',
  phonePattern: /^876-\d{3}-\d{4}$/,

  // Default country code
  countryCode: '+1-876',
};

// ==================== CACHE CONFIGURATION ====================
export const CACHE_CONFIG = {
  // Weather cache TTL (10 minutes)
  weatherTTL: 10 * 60 * 1000,

  // Session storage TTL
  sessionTTL: 24 * 60 * 60 * 1000, // 24 hours

  // Default fetch timeout
  defaultTimeout: 5000,

  // Profile fetch timeout
  profileFetchTimeout: 5000,

  // Service provider load timeout
  serviceProviderTimeout: 10000,
};

// ==================== BREAKPOINTS ====================
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
};

// ==================== LISTING TIERS ====================
export const DEFAULT_LISTING_TIERS = {
  basic: {
    name: 'basic',
    displayName: 'Basic',
    price: 3500,
    currency: 'JMD',
    features: ['Business name & contact', 'Parish listing', 'Basic visibility'],
  },
  premium: {
    name: 'premium',
    displayName: 'Premium',
    price: 5000,
    currency: 'JMD',
    features: ['All Basic features', 'Priority placement', 'Logo display', 'Extended description'],
  },
};

// ==================== VALIDATION THRESHOLDS ====================
export const VALIDATION = {
  // Minimum confirmations for verified status
  minConfirmations: 3,

  // Maximum flags before auto-deactivation
  maxFlags: 3,

  // Reputation score thresholds
  reputation: {
    trusted: 100,
    regular: 50,
    active: 20,
  },
};

// ==================== API LIMITS ====================
export const API_LIMITS = {
  // Maximum results per page
  pageSize: 20,

  // Maximum search results
  maxSearchResults: 100,

  // Maximum file upload size (5MB)
  maxFileSize: 5 * 1024 * 1024,

  // Maximum toast notifications
  maxToasts: 5,
};

// ==================== JAMAICA PARISHES ====================
export const PARISHES = [
  'Kingston',
  'St. Andrew',
  'St. Thomas',
  'Portland',
  'St. Mary',
  'St. Ann',
  'Trelawny',
  'St. James',
  'Hanover',
  'Westmoreland',
  'St. Elizabeth',
  'Manchester',
  'Clarendon',
  'St. Catherine',
];

// ==================== THEME COLORS ====================
// Mirrors the `brand` scale in tailwind.config.js. Nothing imports this today,
// but it was still the old emerald — left alone it is a loaded gun for the next
// person who needs a hex value in a canvas, chart or e-mail template.
export const THEME = {
  primary: {
    50: '#eff3f8',
    100: '#dbe4ef',
    500: '#446c97',
    600: '#33557c',
    700: '#2a4665',
    800: '#253b54',
    900: '#1d2d40',
  },
  accent: {
    400: '#facc15',
    500: '#eab308',
    600: '#ca8a04',
  },
};

// ==================== ANALYTICS ====================
export const ANALYTICS = {
  // GA4 placeholder - to be replaced with actual ID
  ga4MeasurementIdPlaceholder: 'G-XXXXXXXXXX',

  // Event names
  events: {
    pageView: 'page_view',
    offenceView: 'view_item',
    directoryView: 'view_item',
    directoryClick: 'select_content',
    adImpression: 'ad_impression',
    adClick: 'ad_click',
    search: 'search',
  },
};

export default {
  APP_CONFIG,
  CACHE_CONFIG,
  BREAKPOINTS,
  DEFAULT_LISTING_TIERS,
  VALIDATION,
  API_LIMITS,
  PARISHES,
  THEME,
  ANALYTICS,
};
